import React, { useEffect, useState, useRef, useCallback, useMemo } from 'react';
import { liveChatAPI } from './api';
import liveChatSocket from './services/liveChatSocket';
import './LiveChat.css';

const STATUS_TABS = [
    { key: 'waiting', label: 'Waiting' },
    { key: 'active', label: 'Active' },
    { key: 'closed', label: 'Closed' },
    { key: 'all', label: 'All' }
];

function relativeTime(iso) {
    if (!iso) return '';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '';
    const diff = Math.max(0, Math.floor((Date.now() - t) / 1000));
    if (diff < 10) return 'just now';
    if (diff < 60) return `${diff}s ago`;
    if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
    return `${Math.floor(diff / 86400)}d ago`;
}

function formatTime(iso) {
    if (!iso) return '';
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return '';
    return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
}

function initials(name) {
    if (!name) return '?';
    return name
        .split(/\s+/)
        .filter(Boolean)
        .slice(0, 2)
        .map((p) => p[0].toUpperCase())
        .join('');
}

let beepCtx = null;
function playBeep() {
    try {
        if (!beepCtx) {
            const Ctx = window.AudioContext || window.webkitAudioContext;
            if (!Ctx) return;
            beepCtx = new Ctx();
        }
        const osc = beepCtx.createOscillator();
        const gain = beepCtx.createGain();
        osc.connect(gain);
        gain.connect(beepCtx.destination);
        osc.type = 'sine';
        osc.frequency.value = 880;
        gain.gain.value = 0.05;
        osc.start();
        gain.gain.exponentialRampToValueAtTime(0.0001, beepCtx.currentTime + 0.25);
        osc.stop(beepCtx.currentTime + 0.26);
    } catch (e) {
        /* noop */
    }
}

export default function LiveChat() {
    const [filter, setFilter] = useState('waiting');
    const [conversations, setConversations] = useState([]);
    const [selected, setSelected] = useState(null);
    const [messages, setMessages] = useState([]);
    const [draft, setDraft] = useState('');
    const [loadingList, setLoadingList] = useState(false);
    const [loadingMsgs, setLoadingMsgs] = useState(false);
    const [stats, setStats] = useState({ active_count: 0, waiting_count: 0, closed_today: 0, total_today: 0, unread_count: 0 });
    const [typingFromVisitor, setTypingFromVisitor] = useState(null);
    const [connState, setConnState] = useState('disconnected');

    const messagesEndRef = useRef(null);
    const typingTimeoutRef = useRef(null);
    const selectedRef = useRef(null);
    const adminId = useRef(null);

    useEffect(() => {
        try {
            const u = JSON.parse(localStorage.getItem('user') || 'null');
            adminId.current = u?.id || null;
        } catch (e) {
            adminId.current = null;
        }
    }, []);

    useEffect(() => { selectedRef.current = selected; }, [selected]);

    const fetchList = useCallback(async (status) => {
        setLoadingList(true);
        try {
            const res = await liveChatAPI.listConversations({ status, limit: 50 });
            setConversations(res.data?.data?.conversations || []);
        } catch (err) {
            console.error('[LiveChat] list error:', err);
            setConversations([]);
        } finally {
            setLoadingList(false);
        }
    }, []);

    const fetchStats = useCallback(async () => {
        try {
            const res = await liveChatAPI.stats();
            setStats(res.data?.data || stats);
        } catch (err) {
            /* noop */
        }
    }, [stats]);

    useEffect(() => { fetchList(filter); }, [filter, fetchList]);
    useEffect(() => {
        fetchStats();
        const t = setInterval(fetchStats, 30000);
        return () => clearInterval(t);
    }, [fetchStats]);

    // Socket.IO setup
    useEffect(() => {
        const token = localStorage.getItem('token');
        if (!token) return;

        liveChatSocket.connect(token, adminId.current);

        const offConnect = liveChatSocket.on('connect', () => setConnState('connected'));
        const offDisc = liveChatSocket.on('disconnect', () => setConnState('disconnected'));
        const offErr = liveChatSocket.on('connect_error', () => setConnState('error'));

        const offNew = liveChatSocket.on('conversation:new', (conv) => {
            console.log('[LiveChat] conversation:new', conv);
            setStats((s) => ({ ...s, waiting_count: (s.waiting_count || 0) + 1 }));
            const matchesFilter = filter === 'waiting' || filter === 'all';
            if (matchesFilter) {
                setConversations((prev) => {
                    if (prev.some((c) => c.id === conv.conversation_id)) return prev;
                    return [
                        {
                            id: conv.conversation_id,
                            visitor_id: conv.visitor_id,
                            visitor_name: conv.visitor_name,
                            visitor_email: conv.visitor_email,
                            admin_id: null,
                            status: 'waiting',
                            started_at: conv.started_at,
                            last_message_at: conv.started_at,
                            closed_at: null,
                            last_message: null,
                            last_sender_type: null,
                            unread_count: 0
                        },
                        ...prev
                    ];
                });
            }
            if (document.visibilityState !== 'visible') playBeep();
        });

        const offMsg = liveChatSocket.on('message:new', (msg) => {
            console.log('[LiveChat] message:new', msg);
            const sel = selectedRef.current;
            if (sel && sel.id === msg.conversation_id) {
                setMessages((prev) => {
                    if (prev.some((m) => m.id === msg.id)) return prev;
                    return [...prev, msg];
                });
            }
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === msg.conversation_id
                        ? {
                            ...c,
                            last_message: msg.message,
                            last_sender_type: msg.sender_type,
                            last_message_at: msg.created_at,
                            unread_count:
                                msg.sender_type === 'visitor' && (!sel || sel.id !== msg.conversation_id)
                                    ? (c.unread_count || 0) + 1
                                    : c.unread_count
                        }
                        : c
                )
            );
            if (msg.sender_type === 'visitor') {
                setStats((s) => ({ ...s, unread_count: (s.unread_count || 0) + 1 }));
                if (document.visibilityState !== 'visible' || !sel || sel.id !== msg.conversation_id) {
                    playBeep();
                }
            }
        });

        const offClaim = liveChatSocket.on('conversation:claimed', (data) => {
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === data.conversation_id
                        ? { ...c, admin_id: data.admin_id, status: 'active' }
                        : c
                )
            );
            const sel = selectedRef.current;
            if (sel && sel.id === data.conversation_id) {
                setSelected((s) => (s ? { ...s, admin_id: data.admin_id, status: 'active' } : s));
            }
        });

        const offClose = liveChatSocket.on('conversation:closed', (data) => {
            setConversations((prev) =>
                prev.map((c) =>
                    c.id === data.conversation_id ? { ...c, status: 'closed' } : c
                )
            );
            const sel = selectedRef.current;
            if (sel && sel.id === data.conversation_id) {
                setSelected((s) => (s ? { ...s, status: 'closed' } : s));
            }
        });

        const offTyping = liveChatSocket.on('typing', (data) => {
            const sel = selectedRef.current;
            if (data.sender_type !== 'visitor') return;
            if (!sel || sel.id !== data.conversation_id) return;
            setTypingFromVisitor(data.conversation_id);
            clearTimeout(typingTimeoutRef.current);
            typingTimeoutRef.current = setTimeout(() => setTypingFromVisitor(null), 2500);
        });

        return () => {
            offConnect(); offDisc(); offErr();
            offNew(); offMsg(); offClaim(); offClose(); offTyping();
        };
    }, [filter]);

    useEffect(() => {
        if (messagesEndRef.current) {
            messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
        }
    }, [messages, typingFromVisitor]);

    const selectConversation = useCallback(async (conv) => {
        setSelected(conv);
        setMessages([]);
        setTypingFromVisitor(null);
        setLoadingMsgs(true);
        try {
            const res = await liveChatAPI.getMessages(conv.id, { limit: 100 });
            const list = res.data?.data?.messages || [];
            const cleared = res.data?.data?.marked_read || 0;
            setMessages(list);
            if (cleared > 0) {
                setConversations((prev) =>
                    prev.map((c) => (c.id === conv.id ? { ...c, unread_count: 0 } : c))
                );
                setStats((s) => ({ ...s, unread_count: Math.max(0, (s.unread_count || 0) - cleared) }));
            }
        } catch (err) {
            console.error('[LiveChat] fetch messages error:', err);
        } finally {
            setLoadingMsgs(false);
        }
    }, []);

    const handleClaim = useCallback(() => {
        if (!selected) return;
        liveChatSocket.emit('admin:claim', { conversation_id: selected.id }, (ack) => {
            if (!ack?.ok) console.warn('[LiveChat] claim failed:', ack);
        });
    }, [selected]);

    const handleClose = useCallback(() => {
        if (!selected) return;
        if (!window.confirm('End this conversation?')) return;
        liveChatSocket.emit('admin:close', { conversation_id: selected.id }, (ack) => {
            if (!ack?.ok) console.warn('[LiveChat] close failed:', ack);
        });
    }, [selected]);

    const sendMessage = useCallback(() => {
        if (!selected || !draft.trim()) return;
        const text = draft.trim();
        setDraft('');
        liveChatSocket.emit(
            'admin:message',
            { conversation_id: selected.id, message: text },
            (ack) => {
                if (!ack?.ok) {
                    console.warn('[LiveChat] send failed:', ack);
                    setDraft(text);
                }
            }
        );
    }, [selected, draft]);

    const handleKey = useCallback(
        (e) => {
            if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                sendMessage();
            }
        },
        [sendMessage]
    );

    const handleTyping = useCallback(() => {
        if (!selected) return;
        liveChatSocket.emit('typing', { conversation_id: selected.id, sender_type: 'admin' });
    }, [selected]);

    const inputDisabled = !selected || selected.status === 'closed' || (selected.status === 'waiting' && !selected.admin_id);

    const visibleConvs = useMemo(() => conversations, [conversations]);

    return (
        <div className="lc-container">
            {/* Sidebar */}
            <aside className="lc-sidebar">
                <div className="lc-sidebar__header">
                    <div className="lc-sidebar__title">
                        <h2>💬 Live Chat</h2>
                        {stats.unread_count > 0 && <span className="lc-badge">{stats.unread_count}</span>}
                    </div>
                    <div className="lc-tabs">
                        {STATUS_TABS.map((t) => (
                            <button
                                key={t.key}
                                className={`lc-tab ${filter === t.key ? 'lc-tab--active' : ''}`}
                                onClick={() => setFilter(t.key)}
                            >
                                {t.label}
                                {t.key === 'waiting' && stats.waiting_count > 0 ? ` · ${stats.waiting_count}` : ''}
                                {t.key === 'active' && stats.active_count > 0 ? ` · ${stats.active_count}` : ''}
                            </button>
                        ))}
                    </div>
                </div>

                <div className="lc-list">
                    {loadingList ? (
                        <div className="lc-list__loading">Loading conversations…</div>
                    ) : visibleConvs.length === 0 ? (
                        <div className="lc-list__empty">No conversations</div>
                    ) : (
                        visibleConvs.map((c) => (
                            <div
                                key={c.id}
                                className={`lc-conv ${selected?.id === c.id ? 'lc-conv--selected' : ''}`}
                                onClick={() => selectConversation(c)}
                            >
                                <div className="lc-avatar">
                                    {initials(c.visitor_name)}
                                    <span className={`lc-avatar__dot lc-avatar__dot--${c.status}`} />
                                </div>
                                <div className="lc-conv__body">
                                    <div className="lc-conv__row">
                                        <span className="lc-conv__name">{c.visitor_name}</span>
                                        <span className="lc-conv__time">{relativeTime(c.last_message_at)}</span>
                                    </div>
                                    {c.visitor_email && <div className="lc-conv__email">{c.visitor_email}</div>}
                                    <div className="lc-conv__preview">
                                        <span className="lc-conv__msg">
                                            {c.last_message
                                                ? `${c.last_sender_type === 'admin' ? 'You: ' : ''}${c.last_message}`
                                                : <em style={{ color: '#bbb' }}>No messages yet</em>}
                                        </span>
                                        {c.unread_count > 0 && <span className="lc-unread">{c.unread_count}</span>}
                                    </div>
                                </div>
                            </div>
                        ))
                    )}
                </div>
            </aside>

            {/* Chat window */}
            <section className="lc-chat">
                {selected ? (
                    <>
                        <div className="lc-chat__header">
                            <div className="lc-chat__hinfo">
                                <h3>
                                    {selected.visitor_name}
                                    <span className={`lc-chip lc-chip--${selected.status}`}>{selected.status}</span>
                                </h3>
                                <p>
                                    {selected.visitor_email || 'no email'} · ID {selected.visitor_id}
                                </p>
                            </div>
                            <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                                <span className={`lc-conn-pill lc-conn-pill--${connState === 'connected' ? 'ok' : 'err'}`}>
                                    {connState === 'connected' ? 'Online' : 'Connecting…'}
                                </span>
                                {selected.status === 'waiting' && !selected.admin_id && (
                                    <button className="lc-btn lc-btn--primary" onClick={handleClaim}>Claim</button>
                                )}
                                {selected.status === 'active' && (
                                    <button className="lc-btn lc-btn--danger" onClick={handleClose}>End Chat</button>
                                )}
                            </div>
                        </div>

                        <div className="lc-messages">
                            {loadingMsgs ? (
                                <div className="lc-list__loading">Loading messages…</div>
                            ) : messages.length === 0 ? (
                                <div className="lc-empty-chat">
                                    <div className="lc-empty-chat__icon">💬</div>
                                    <div>No messages yet — be the first to say hi.</div>
                                </div>
                            ) : (
                                messages.map((m) => (
                                    <div key={m.id} className={`lc-msg lc-msg--${m.sender_type}`}>
                                        <div className="lc-msg__bubble">
                                            {m.message}
                                            <span className="lc-msg__time">{formatTime(m.created_at)}</span>
                                        </div>
                                    </div>
                                ))
                            )}
                            {typingFromVisitor === selected.id && (
                                <div className="lc-typing">
                                    <span /><span /><span />
                                </div>
                            )}
                            <div ref={messagesEndRef} />
                        </div>

                        <div className="lc-input">
                            <textarea
                                rows={1}
                                placeholder={
                                    inputDisabled
                                        ? selected.status === 'closed'
                                            ? 'Conversation closed'
                                            : 'Claim this chat to start replying'
                                        : 'Type a message — Enter to send, Shift+Enter for new line'
                                }
                                value={draft}
                                disabled={inputDisabled}
                                onChange={(e) => { setDraft(e.target.value); handleTyping(); }}
                                onKeyDown={handleKey}
                            />
                            <button
                                className="lc-btn lc-btn--primary"
                                onClick={sendMessage}
                                disabled={inputDisabled || !draft.trim()}
                            >
                                Send
                            </button>
                        </div>
                    </>
                ) : (
                    <div className="lc-empty-chat">
                        <div className="lc-empty-chat__icon">📨</div>
                        <h3 style={{ margin: '0 0 6px', color: '#444' }}>Select a conversation</h3>
                        <p style={{ margin: 0, color: '#888' }}>
                            Choose a conversation from the left to start chatting with a visitor.
                        </p>
                    </div>
                )}
            </section>
        </div>
    );
}
